import React from 'react';
import { SxProps, FormControl, Select, SelectChangeEvent, MenuItem } from '@mui/material';

type Props<T> = {
  availableNames: string[];
  availableValues: T[];
  selectedValue: T;
  setSelectedValue: React.Dispatch<React.SetStateAction<T>>;
  loading?: boolean;
  selectStyleOverrides?: SxProps;
  itemStyleOverrides?: SxProps[];
};

/**
 * Dropdown component for selecting one item from a list
 * 
 * @param availableNames: string[] - list of available names
 * @param availableValues: T[] - list of available values
 * @param selectedValue: T - currently selected value
 * @param setSelectedValue: React.Dispatch<React.SetStateAction<T>> - set state for selected value in parent component
 * @param loading?: boolean - whether the dropdown is loading
 * @param selectStyleOverride?: SxProps - override for the select style
 * @returns Dropdown element
 */
export const Dropdown = <T,>({
  availableNames,
  availableValues,
  selectedValue,
  setSelectedValue,
  loading=false,
  selectStyleOverrides,
  itemStyleOverrides,
}: Props<T>): JSX.Element => {

  const selectedIndex = availableValues.indexOf(selectedValue)

  const handleChange = (e: SelectChangeEvent<string>) => {
    const value = availableValues[Number(e.target.value)];
    setSelectedValue(value);
  };
  
  const selectStyle: SxProps = {
    minWidth: 100,
    backgroundColor: "#eef2f8",
    margin: "auto",
    ...selectStyleOverrides,
  };

  return (
    <FormControl>
      <Select
        sx={selectStyle}
        value={selectedIndex >= 0 ? String(selectedIndex) : ""}
        onChange={handleChange}
        renderValue={(value) => {
          return value === "" ? (loading ? 'Loading...' : 'Select an option...') : availableNames[Number(value)];
        }}
        displayEmpty
      >
        {availableNames.map((name, i) => (
          <MenuItem sx={itemStyleOverrides?.[i]} key={i} value={String(i)}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default Dropdown;
